import React, { useRef, useEffect, useState } from 'react';
import '@/pages/home/assets/scss/components/robotBanner.scss';

interface RobotBannerProps {
  containerRef?: React.RefObject<HTMLElement>;
}

const Robot: React.FC<RobotBannerProps> = ({ containerRef }) => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<HTMLElement[]>([]);
  const personRef = useRef<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPersonAnimating, setIsPersonAnimating] = useState(false);

  useEffect(() => {
    // 优先使用传入的 containerRef，否则使用自身的 bannerRef
    const container = containerRef?.current || bannerRef.current;
    const person = personRef.current;
    if (!container) return;

    // 不同元素的视差系数（数值越大，移动幅度越大）
    const parallaxFactors = [0.3, 0.2, 0.3, 0.2, 0.1]; // 对应 star01, star02, star03, game, person

    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left; // 鼠标相对于 container 的 X 坐标
      const centerX = rect.width / 2; // container 中心 X 坐标
      const offsetX = mouseX - centerX; // 鼠标相对中心的偏移量
      const maxOffset = rect.width / 2;
      const normalizedX = offsetX / maxOffset; // 归一化到 -1 ~ 1

      // 为每个元素应用不同的视差偏移（只有 X 轴）
      elementsRef.current.forEach((element, index) => {
        if (element) {
          const factor = parallaxFactors[index] || 0.2;
          const translateX = normalizedX * 120 * factor; // 120 是最大偏移像素
          element.style.transform = `translateX(${translateX}px)`;
        }
      });
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      // 鼠标离开时恢复原位
      elementsRef.current.forEach((element) => {
        if (element) {
          element.style.transform = 'translateX(0px)';
        }
      });
    };

    // Person 元素的悬停事件
    const handlePersonMouseEnter = () => {
      setIsPersonAnimating(true);
    };

    const handlePersonAnimationEnd = () => {
      setIsPersonAnimating(false);
    };

    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    if (person) {
      person.addEventListener('mouseenter', handlePersonMouseEnter);
      person.addEventListener('animationend', handlePersonAnimationEnd);
    }

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      
      if (person) {
        person.removeEventListener('mouseenter', handlePersonMouseEnter);
        person.removeEventListener('animationend', handlePersonAnimationEnd);
      }
    };
  }, [containerRef]);

  return (
    <div 
      className={`dbh-robot-banner ${isHovered ? 'dbh-robot-banner-hover' : ''}`} 
      ref={bannerRef}
    >
      <div className="dbh-robot-banner-wrap">
        <i className="dbh-robot-star01" ref={(el) => el && (elementsRef.current[0] = el)}></i>
        <i className="dbh-robot-star02" ref={(el) => el && (elementsRef.current[1] = el)}></i>
        <i className="dbh-robot-star03" ref={(el) => el && (elementsRef.current[2] = el)}></i>
        <i className="dbh-robot-game" ref={(el) => el && (elementsRef.current[3] = el)}></i>
        <i 
          className={`dbh-robot-person ${isPersonAnimating ? 'is-animating' : ''}`}
          ref={(el) => {
            if (el) {
              elementsRef.current[4] = el;
              personRef.current = el;
            }
          }}
        ></i>
      </div>
    </div>
  );
};

export default Robot;
